import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import { SendTemplateRequest } from '@/Domain/api/WhatsAppTemplates/WhatsAppTemplates.api';

export const useWhatsAppTemplates = (enabled: boolean = true) => {
  const query = useQuery({
    queryKey: [QueryKey.whatsappTemplates.list],
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    retry: false, // 404 (no integration) / 409 (WABA inaccessible) won't succeed on retry
    enabled,
  });

  return {
    templates: query.data?.templates ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useSendWhatsAppTemplate = (conversationId: string) => {
  const { t } = useTranslation(['inbox_v2']);
  const queryClient = useQueryClient();

  const mutation = useMutation<
    unknown,
    AxiosError,
    { conversationId: string } & SendTemplateRequest
  >({
    mutationKey: [QueryKey.whatsappTemplates.send],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [Endpoint.conversation.details],
      });
      toast.success(t('whatsapp_template.toast.send_success'));
    },
    onError: (error: AxiosError) => {
      toast.error(
        get(error, 'response.data.errors.root[0].message') ||
          t('whatsapp_template.toast.send_error'),
      );
    },
  });

  const handleSendTemplate = (data: SendTemplateRequest) =>
    mutation.mutate({ conversationId, ...data });

  const sendTemplateAsync = (data: SendTemplateRequest) =>
    mutation.mutateAsync({ conversationId, ...data });

  return {
    handleSendTemplate,
    sendTemplateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};
